import { useEffect, useMemo, useState } from 'react';
import useAsyncEffect from '../utils/useAsyncEffect';
import { QueryDataOptions, useQuery, QueryResult } from '@apollo/client';
import get from 'lodash.get';
import fetchQueryJobStatus from '../../util/fetchQueryJobStatus';
import { OrderByNode } from '@jupiterone/query-language-parser';
import { formatJ1OrderByQuery } from '../../util/formatJ1Query';
import {
  J1QLResultData,
  isJ1QLTreeData,
  J1QLListData,
} from '../../../types/J1QL';

const createJ1QLQueryInput = ({
  queryString,
  orderByNode,
}: {
  queryString: string;
  orderByNode?: OrderByNode;
}) => {
  if (orderByNode) {
    return formatJ1OrderByQuery(queryString, orderByNode);
  } else {
    return queryString;
  }
};

export interface UseJ1QLWithCursorPaginationProps<T>
  extends QueryDataOptions<T> {
  deferred?: boolean;
  dataPath?: string;
  cursorPath: string;
  mergeDataResponses: (
    previousData: T | undefined,
    newData: T | undefined
  ) => T | undefined;
}

export interface UseJ1QLWithCursorPaginationResult<T> {
  result?: T;
  removeResultById: (id: string) => void;
  setOrderByNode: (orderByNode?: OrderByNode) => void;
  loading: QueryResult['loading'];
  totalResultCount?: number;
  fetchMore: () => Promise<void>;
  canFetchMore: boolean;
  nextCursor?: string;
  called?: boolean;
  refetch: QueryResult['refetch'];
  error?: Error;
  // Supporting this portion of the old interface for now, should remove when all consumers have moved to the `result` above
  untypedJ1QLResult?: any;
}

interface State<T> {
  resultData?: T;
  totalResultCount?: number;
  nextCursor?: string;
  error?: Error;
  loading: boolean;
  orderByNode?: OrderByNode;
}

const useJ1QLWithCursorPagination = <T extends J1QLResultData>({
  query,
  deferred = false,
  dataPath,
  cursorPath,
  mergeDataResponses,
  ...apolloSettings
}: UseJ1QLWithCursorPaginationProps<T>): UseJ1QLWithCursorPaginationResult<T> => {
  const [state, setState] = useState<State<T>>({
    loading: !apolloSettings.skip,
  });

  const j1qlQuery = useMemo(() => {
    return createJ1QLQueryInput({
      queryString: apolloSettings.variables?.query,
      orderByNode: state.orderByNode,
    });
  }, [state.orderByNode, apolloSettings.variables?.query]);

  const apolloSettingsInput = { ...apolloSettings };

  if (!apolloSettingsInput.variables) {
    apolloSettingsInput.variables = {};
  }

  apolloSettingsInput.variables.query = j1qlQuery;

  const queryResponse = useQuery(query, apolloSettingsInput);

  useEffect(() => {
    // When a new query is passed in, make sure we clear our internal
    // state so that we can receive the new results correctly
    setState((prevState) => ({
      ...prevState,
      nextCursor: undefined,
      resultData: undefined,
      totalResultCount: undefined,
    }));
  }, [apolloSettings.variables?.query]);

  const handleNewQueryResponse = (response: Pick<QueryResult<T>, 'data'>) => {
    const newCursor = get(response.data, cursorPath);

    const newRawResultData: T | undefined = dataPath
      ? get(response.data, dataPath)
      : response.data;

    handleNewValues(newRawResultData, newCursor, newRawResultData?.totalCount);
  };

  const handleNewValues = (
    data: T | undefined,
    cursor: string | null | undefined,
    totalCount: number | null | undefined
  ) => {
    // newCursor could be null, so make sure we coerce it to undefined
    const nextCursor = cursor ?? undefined;
    const newTotalCount = totalCount ?? undefined;

    let resultDataToSet: T | undefined = undefined;

    if (!state.resultData) {
      resultDataToSet = data;
    } else {
      if (data) {
        // Take all ofthe results from the new query response, but keep the old
        // data (so we can carefully merge it below)
        const mergedResultData = mergeDataResponses(state.resultData, data);

        resultDataToSet = mergedResultData;
      } else {
        setState((prevState) => ({
          ...prevState,
          error: new Error('Incomplete data set returned.'),
        }));
        return;
      }
    }

    setState((prevState) => {
      const newState = {
        ...prevState,
        loading: false,
        resultData: resultDataToSet,
        nextCursor,
      };

      // Total count is an interesting edge case.  It only has the possibility
      // of being defined on the FIRST return from a J1QL query.  Any subsequent
      // pagination requests will nave the totalCount field undefined, so we need
      // to keep around the totalCount from the beginning
      if (newTotalCount !== undefined || nextCursor === undefined) {
        newState.totalResultCount = newTotalCount;
      }
      return newState;
    });
  };

  const handleNewDeferredQueryResponse = async (
    response: Pick<QueryResult, 'data'>
  ) => {
    let errorDuringDeferred: Error | undefined = undefined;

    const stateFileUrl = get(response.data, `${dataPath}.url`);

    if (stateFileUrl) {
      try {
        const dataUrl = await fetchQueryJobStatus({
          pollFrequency: 2500,
          stateFileUrl,
        });

        if (dataUrl) {
          const j1QLResponse = await fetch(dataUrl);

          const j1QLResultData = (await j1QLResponse.json()) as T;

          handleNewValues(
            j1QLResultData,
            j1QLResultData.cursor,
            j1QLResultData.totalCount
          );
        }
      } catch (error) {
        errorDuringDeferred = new Error(
          `Error loading result for query ${apolloSettings.variables?.queryInput}`
        );
      }
    } else {
      errorDuringDeferred = new Error(
        'No state file returned by j1ql deferred.'
      );
    }

    if (errorDuringDeferred) {
      setState((prevState) => ({
        ...prevState,
        error: errorDuringDeferred,
      }));
    }
  };

  const handleGenericResponse = async (
    response: Pick<QueryResult<T>, 'data'>
  ) => {
    if (deferred) {
      setState((prevState) => ({ ...prevState, loading: true }));
      await handleNewDeferredQueryResponse(response);
    } else {
      handleNewQueryResponse(response);
    }
  };

  useAsyncEffect(async () => {
    if (!queryResponse.loading && queryResponse.data) {
      await handleGenericResponse(queryResponse);
    }
  }, [queryResponse.loading, queryResponse.data]);

  const fetchMore = async () => {
    if (state.nextCursor) {
      setState((prevState) => ({ ...prevState, loading: true }));
      const fetchMoreResponse = await queryResponse.fetchMore({
        variables: { cursor: state.nextCursor },
      });

      await handleGenericResponse(fetchMoreResponse);
    }
  };

  const removeResultById = (idToDelete: string) => {
    setState((prevState) => {
      const { resultData } = prevState;
      if (resultData && resultData.data) {
        if (isJ1QLTreeData(resultData)) {
          const newData = {
            ...prevState.resultData,
          };

          newData.data = {
            vertices: resultData.data.vertices.filter(
              (vertex) => vertex.id !== idToDelete
            ),
            edges: resultData.data.edges.filter(
              (edge) => edge.id !== idToDelete
            ),
          };

          return {
            ...prevState,
            resultData: newData,
          } as State<T>;
        } else {
          const newData = {
            ...prevState.resultData,
          };

          newData.data = (resultData as J1QLListData).data!.filter(
            (vertex) => vertex.id !== idToDelete
          );
          return {
            ...prevState,
            resultData: newData,
          } as State<T>;
        }
      }

      return prevState;
    });
  };

  const setOrderByNode = (orderByNode?: OrderByNode) => {
    setState((prevState) => ({
      ...prevState,
      orderByNode,
    }));
  };

  const refetch = async () => {
    return queryResponse.refetch();
  };

  return {
    result: state.resultData,
    removeResultById: removeResultById,
    setOrderByNode,
    nextCursor: state.nextCursor,
    canFetchMore: !queryResponse.loading && Boolean(state.nextCursor),
    fetchMore,
    error: state.error,
    loading: queryResponse.loading || state.loading,
    refetch,
    called: queryResponse.called,
    totalResultCount: state.totalResultCount,
    // Used to support legacy interfaces - will remove soon hopefully
    untypedJ1QLResult: queryResponse.data,
  };
};

export default useJ1QLWithCursorPagination;
